import { FC, useState } from 'react';
import classNames from 'classnames';
import { Button, Spin, PlusOutlined, Popover, Tag } from '@fx-ui/fine-design';
import { DataType } from '@fx-ui/jdy-design-biz';
import { includes, isEmpty, isEqual, keys, omitBy } from '@libs/shared';
import { CorpGroupConfig, CorpGroupType, RangeType } from '../../interfaces';
import { platformIntl } from '../../constants';
import { getContactDeptList, getContactMemberList, getContactRoleList } from '../../apis';
import ContactValuePanel from '../contact-value-panel';
import OpCheck from './components/op-check';
import RangePicker from './components/range-picker';
import ConfigLine from './components/config-line';
import {
    getCoopAuthTextValue,
    getDeptAuthTextValue,
    getExternalAuthTextValue,
    getRoleAuthTextValue
} from './utils';
import { hasFunction } from '../../utils';
import MemberSelectDialog from '../member-select-dialog';
import DeptSelectDialog from '../dept-select-dialog';
import RoleSelectDialog from '../role-select-dialog';
import MemberName from '../member-name';
import MemberAvatar from '../member-avatar';
import { authModel } from '../../models';

interface CorpManagerPanelProps {
    className?: string;
    /** 配置项标签位置 */
    labelPosition?: 'top' | 'left';
    /** 只读不可配置 */
    readonly?: boolean;
    /** 不展示管理员项 */
    noMembers?: boolean;
    /** 不展示加载中/加载成功提示 */
    noLoadingTip?: boolean;
    illegalMemberIds?: string[];
    highlightMemberIds?: string[];
    groupConfig?: CorpGroupConfig;
    onUpdate?: (key: string, value: any) => Promise<boolean>;
    onMemberSelectVisibleChange?: (visible: boolean) => void;
}

const CorpManagerPanel: FC<CorpManagerPanelProps> = ({
    className,
    labelPosition = 'left',
    readonly,
    noMembers,
    noLoadingTip,
    illegalMemberIds = [],
    highlightMemberIds = [],
    groupConfig,
    onUpdate,
    onMemberSelectVisibleChange
}) => {
    const [lineLoading, setLineLoading] = useState({
        depts: false,
        roles: false,
        externalMembers: false,
        externalRoles: false,
        coops: false
    });
    const [memberSelectVisible, setMemberSelectVisible] = useState(false);
    const [deptSelectVisible, setDeptSelectVisible] = useState(false);
    const [roleSelectVisible, setRoleSelectVisible] = useState(false);

    const isSys = groupConfig?.group_type === CorpGroupType.System;

    const loadingByLine = (key?: keyof typeof lineLoading) => {
        if (!key || noLoadingTip) {
            return () => {};
        }
        setLineLoading((prev) => ({ ...prev, [key]: true }));

        return () => {
            setLineLoading((prev) => ({ ...prev, [key]: false }));
        };
    };

    const updateMemberSelectVisible = (visible: boolean) => {
        setMemberSelectVisible(visible);
        onMemberSelectVisibleChange?.(visible);
    };

    const update = (key: keyof CorpGroupConfig, value: any) => {
        let lineKey: keyof typeof lineLoading | undefined;

        switch (key) {
            case 'dept_range':
            case 'dept_range_type':
                lineKey = 'depts';
                break;
            case 'role_range':
            case 'role_range_type':
                lineKey = 'roles';
                break;
            case 'op':
                {
                    const diffKeys = keys(omitBy(groupConfig?.op, (v, k) => isEqual(v, value[k])));
                    if (diffKeys.includes('memberRead') || diffKeys.includes('memberWrite')) {
                        lineKey = 'depts';
                    } else if (diffKeys.includes('roleRead') || diffKeys.includes('roleWrite')) {
                        lineKey = 'roles';
                    } else if (diffKeys.includes('externalMemberManage')) {
                        lineKey = 'externalMembers';
                    } else if (diffKeys.includes('externalRoleManage')) {
                        lineKey = 'externalRoles';
                    } else if (diffKeys.includes('corpCoopManage')) {
                        lineKey = 'coops';
                    }
                }
                break;
        }

        const endLineLoading = loadingByLine(lineKey);

        onUpdate?.(key, value).then((res) => {
            if (res) {
                switch (key) {
                    case 'members':
                        updateMemberSelectVisible(false);
                        break;
                    case 'dept_range':
                        setDeptSelectVisible(false);
                        break;
                    case 'role_range':
                        setRoleSelectVisible(false);
                }
            }
            endLineLoading();
        });
    };

    if (!groupConfig) {
        return <Spin />;
    }

    const renderEllipsis = (items: any[] = [], dataType: DataType) => {
        return (
            <Popover
                trigger="hover"
                style={{ maxWidth: 250, maxHeight: 360, overflowY: 'auto' }}
                content={
                    <ContactValuePanel
                        className={classNames('fx-corp-manager-ellipsis-panel', dataType)}
                        dataType={dataType}
                        items={items}
                    />
                }
            >
                <Tag className="ellipsis-tag" size="normal" shape="roundedRec">
                    +{items.length}
                </Tag>
            </Popover>
        );
    };

    /**
     * 管理员成员配置
     */
    const renderMembersConfigContent = () => {
        const renderItem = (item: any) => {
            return (
                <Tag
                    key={item._id}
                    className={classNames('value-tag', {
                        highlighted: includes(highlightMemberIds, item._id)
                    })}
                    type={includes(illegalMemberIds, item._id) ? 'error' : 'normal'}
                    shape="roundedRec"
                    hasBorder={includes(highlightMemberIds, item._id)}
                    icon={
                        <MemberAvatar img={item.avatar} name={item.nickname} status={item.status} />
                    }
                >
                    <MemberName
                        className="member-name"
                        name={item.nickname}
                        status={item.status}
                        type={item.type}
                    />
                </Tag>
            );
        };

        if (readonly) {
            if (isEmpty(groupConfig.members)) {
                return '-';
            }

            return (
                <ContactValuePanel
                    className="readonly"
                    ellipsis
                    dataType={DataType.member}
                    items={groupConfig.members}
                    renderItem={renderItem}
                    renderEllipsis={(items) => renderEllipsis(items, DataType.member)}
                />
            );
        }

        if (isEmpty(groupConfig.members)) {
            return (
                <Button
                    className="select-btn"
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={() => updateMemberSelectVisible(true)}
                >
                    {platformIntl.get('select.member')}
                </Button>
            );
        }

        return (
            <ContactValuePanel
                dataType={DataType.member}
                items={groupConfig.members}
                renderItem={renderItem}
                onClick={() => updateMemberSelectVisible(true)}
            />
        );
    };

    /**
     * 内部部门配置
     */
    const renderDeptsConfigContent = () => {
        if (readonly) {
            const showValuePanel =
                groupConfig.op.memberRead &&
                groupConfig.dept_range_type === RangeType.Partial &&
                !isEmpty(groupConfig.dept_range);

            return (
                <>
                    <span>{getDeptAuthTextValue(groupConfig)}</span>
                    {showValuePanel && (
                        <ContactValuePanel
                            ellipsis
                            recursion
                            dataType={DataType.dept}
                            items={groupConfig.dept_range}
                            renderEllipsis={(items) => renderEllipsis(items, DataType.dept)}
                        />
                    )}
                </>
            );
        }

        return (
            <OpCheck
                loading={lineLoading.depts}
                readable={groupConfig.op.memberRead}
                writable={groupConfig.op.memberWrite}
                onReadableChange={(checked) => {
                    update('op', {
                        ...groupConfig.op,
                        memberRead: checked,
                        ...(!checked && { memberWrite: false })
                    });
                }}
                onWritableChange={(checked) => {
                    update('op', {
                        ...groupConfig.op,
                        memberWrite: checked,
                        ...(checked && { memberRead: true })
                    });
                }}
            >
                <RangePicker
                    loading={lineLoading.depts}
                    dataType={DataType.dept}
                    rangeType={groupConfig.dept_range_type}
                    range={groupConfig.dept_range}
                    selectText={platformIntl.get('select.dept')}
                    options={[
                        {
                            label: platformIntl.get('fxp.base.bg.admin.all_dept.opt'),
                            value: RangeType.All
                        },
                        {
                            label: platformIntl.get('fxp.base.bg.admin.part_dept.opt'),
                            value: RangeType.Partial
                        }
                    ]}
                    onSelect={() => setDeptSelectVisible(true)}
                    onSwitch={(type) => update('dept_range_type', type)}
                />
            </OpCheck>
        );
    };

    /**
     * 内部角色配置
     */
    const renderRolesConfigContent = () => {
        if (readonly) {
            const showValuePanel =
                groupConfig.op.roleRead &&
                groupConfig.role_range_type === RangeType.Partial &&
                !isEmpty(groupConfig.role_range);

            return (
                <>
                    <span>{getRoleAuthTextValue(groupConfig)}</span>
                    {showValuePanel && (
                        <ContactValuePanel
                            ellipsis
                            recursion
                            dataType={DataType.role}
                            items={groupConfig.role_range}
                            renderEllipsis={(items) => renderEllipsis(items, DataType.role)}
                        />
                    )}
                </>
            );
        }

        return (
            <OpCheck
                loading={lineLoading.roles}
                readable={groupConfig.op.roleRead}
                writable={groupConfig.op.roleWrite}
                onReadableChange={(checked) => {
                    update('op', {
                        ...groupConfig.op,
                        roleRead: checked,
                        ...(!checked && { roleWrite: false })
                    });
                }}
                onWritableChange={(checked) => {
                    update('op', {
                        ...groupConfig.op,
                        roleWrite: checked,
                        ...(checked && { roleRead: true })
                    });
                }}
            >
                <RangePicker
                    loading={lineLoading.roles}
                    dataType={DataType.role}
                    rangeType={groupConfig.role_range_type}
                    range={groupConfig.role_range}
                    selectText={platformIntl.get('select.role')}
                    options={[
                        {
                            label: platformIntl.get('fxp.base.bg.admin.all_role.opt'),
                            value: RangeType.All
                        },
                        {
                            label: platformIntl.get('fxp.base.bg.admin.part_role.opt'),
                            value: RangeType.Partial
                        }
                    ]}
                    onSelect={() => setRoleSelectVisible(true)}
                    onSwitch={(type) => update('role_range_type', type)}
                />
            </OpCheck>
        );
    };

    /**
     * 互联组织配置
     */
    const renderCoopsConfigContent = () => {
        if (readonly) {
            return getCoopAuthTextValue(groupConfig);
        }

        return (
            <OpCheck
                loading={lineLoading.coops}
                writable={groupConfig.op.corpCoopManage}
                tooltip={platformIntl.get('fxp.base.bg.admin.linked_org.tip')}
                onWritableChange={(checked) => {
                    update('op', { ...groupConfig.op, corpCoopManage: checked });
                }}
            />
        );
    };

    /**
     * 外部用户配置
     */
    const renderExternalMemberConfigContent = () => {
        if (readonly) {
            return getExternalAuthTextValue(groupConfig.op.externalMemberManage);
        }

        return (
            <OpCheck
                loading={lineLoading.externalMembers}
                writable={groupConfig.op.externalMemberManage}
                tooltip={platformIntl.get('fxp.base.bg.admin.manage_scope_extra_user.tip')}
                onWritableChange={(checked) => {
                    update('op', { ...groupConfig.op, externalMemberManage: checked });
                }}
            />
        );
    };

    /**
     * 外部角色配置
     */
    const renderExternalRoleConfigContent = () => {
        if (readonly) {
            return getExternalAuthTextValue(groupConfig.op.externalRoleManage);
        }

        return (
            <OpCheck
                loading={lineLoading.externalRoles}
                writable={groupConfig.op.externalRoleManage}
                tooltip={platformIntl.get('fxp.base.bg.admin.manage_scope_extra_role.tip')}
                onWritableChange={(checked) => {
                    update('op', { ...groupConfig.op, externalRoleManage: checked });
                }}
            />
        );
    };

    return (
        <div
            className={classNames(
                'fx-corp-manager-panel',
                {
                    readonly
                },
                `label-${labelPosition}`,
                className
            )}
        >
            {!noMembers && (
                <ConfigLine
                    className="members"
                    label={platformIntl.get('fxp.base.bg.admin.admin.nm')}
                    content={renderMembersConfigContent()}
                />
            )}
            {isSys ? (
                <ConfigLine
                    className="scope"
                    label={platformIntl.get('fxp.base.bg.admin.permission_scope.nm')}
                    content={platformIntl.get('fxp.base.bg.admin.sys_admin_scope.tip')}
                />
            ) : (
                <>
                    <ConfigLine
                        className="config-group internal"
                        label={platformIntl.get('fxp.base.bg.admin.inner_org.nm')}
                        content={
                            <>
                                <ConfigLine
                                    className="depts"
                                    loading={lineLoading.depts}
                                    label={platformIntl.get('fxp.base.bg.admin.inner_dept.nm')}
                                    content={renderDeptsConfigContent()}
                                />
                                <ConfigLine
                                    className="roles"
                                    loading={lineLoading.roles}
                                    label={platformIntl.get('fxp.base.bg.admin.inner_role.nm')}
                                    content={renderRolesConfigContent()}
                                />
                            </>
                        }
                    />
                    {authModel.isExternalUserAvailable && (
                        <ConfigLine
                            className="config-group external"
                            label={platformIntl.get('fxp.base.bg.extra_user.ttl')}
                            content={
                                <>
                                    <ConfigLine
                                        className="external-members"
                                        loading={lineLoading.externalMembers}
                                        label={platformIntl.get(
                                            'fxp.base.bg.admin.external_member.nm'
                                        )}
                                        content={renderExternalMemberConfigContent()}
                                    />
                                    <ConfigLine
                                        className="external-roles"
                                        loading={lineLoading.externalRoles}
                                        label={platformIntl.get(
                                            'fxp.base.bg.extra_user.extra_role.nm'
                                        )}
                                        content={renderExternalRoleConfigContent()}
                                    />
                                </>
                            }
                        />
                    )}
                    {hasFunction('corp_coop') && (
                        <ConfigLine
                            className="coops"
                            loading={lineLoading.coops}
                            label={platformIntl.get('fxp.base.bg.admin.linked_org.nm')}
                            content={renderCoopsConfigContent()}
                        />
                    )}
                </>
            )}
            {!(readonly || noMembers) && (
                <MemberSelectDialog
                    visible={memberSelectVisible}
                    value={groupConfig.members}
                    errorIds={illegalMemberIds}
                    onFetchInternalDepartments={() => getContactDeptList(authModel.tenantId)}
                    onFetchInternalMembers={(p) =>
                        getContactMemberList(authModel.tenantId, { ...p, hasCount: true })
                    }
                    onOk={(members) => update('members', members)}
                    onCancel={() => updateMemberSelectVisible(false)}
                />
            )}
            {!readonly && !isSys && (
                <>
                    <DeptSelectDialog
                        visible={deptSelectVisible}
                        recursion
                        value={groupConfig.dept_range}
                        onFetchInternalDepartments={(p) =>
                            getContactDeptList(authModel.tenantId, p)
                        }
                        onOk={(departments) => update('dept_range', departments)}
                        onCancel={() => setDeptSelectVisible(false)}
                    />
                    <RoleSelectDialog
                        visible={roleSelectVisible}
                        value={groupConfig.role_range}
                        onFetchInternalRoles={(p) =>
                            getContactRoleList(authModel.tenantId, { ...p, hasCount: true })
                        }
                        onOk={(roles) => update('role_range', roles)}
                        onCancel={() => setRoleSelectVisible(false)}
                    />
                </>
            )}
        </div>
    );
};
export default CorpManagerPanel;
